import { addDays, endOfDay } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

const PARIS_TZ = 'Europe/Paris'

/**
 * Returns the UTC instant corresponding to the end (23:59:59.999, Paris TZ) of
 * the Monday that closes the current weekend, relative to `now`:
 * - Saturday / Sunday -> the upcoming Monday
 * - Monday            -> the same day
 * This is the latest moment a deadline can still lapse before the team is able
 * to act again (Monday EOD).
 */
function endOfClosingMondayParis(now: Date): Date {
  const paris = toZonedTime(now, PARIS_TZ)
  const day = paris.getDay() // 0=Sun, 1=Mon, 6=Sat
  const daysUntilMonday = day === 1 ? 0 : (1 - day + 7) % 7
  const mondayEndWall = endOfDay(addDays(paris, daysUntilMonday))
  return fromZonedTime(mondayEndWall, PARIS_TZ)
}

/**
 * Checks if a deadline is critical for the current weekend, i.e. it both:
 * 1. falls on a day that cannot be handled before Monday EOD — Saturday,
 *    Sunday, or Monday (Paris TZ), and
 * 2. actually lands within the un-actionable window: between `now` and the end
 *    of the Monday closing this weekend.
 *
 * The horizon check (2) prevents false positives where an OPW/LD that is months
 * away (or already expired) merely happens to fall on a Sat/Sun/Mon by calendar
 * coincidence.
 */
export function isDeadlineCriticalForWeekend(deadline: Date, now: Date = new Date()): boolean {
  const paris = toZonedTime(deadline, PARIS_TZ)
  const day = paris.getDay() // 0=Sun, 1=Mon, 6=Sat
  if (day !== 0 && day !== 1 && day !== 6) return false

  const closingMondayEnd = endOfClosingMondayParis(now)
  return deadline.getTime() >= now.getTime() && deadline.getTime() <= closingMondayEnd.getTime()
}

/**
 * Checks if a deadline date falls on a Saturday or Sunday (Paris TZ).
 */
export function isDeadlineOnWeekend(deadline: Date): boolean {
  const paris = toZonedTime(deadline, PARIS_TZ)
  const day = paris.getDay()
  return day === 0 || day === 6
}

/**
 * Checks if the current time (Paris TZ) is within the weekend alert window:
 * Saturday >= 11:30 through end of Sunday 23:59.
 */
export function isWeekendAlertWindow(now: Date = new Date()): boolean {
  const paris = toZonedTime(now, PARIS_TZ)
  const day = paris.getDay() // 0=Sun, 6=Sat

  if (day === 0) return true
  if (day === 6) {
    const hours = paris.getHours()
    const minutes = paris.getMinutes()
    return hours > 11 || (hours === 11 && minutes >= 30)
  }
  return false
}

/**
 * Returns true if both weekend booking alert conditions are met:
 * 1. Current time is in the weekend alert window (Sat >= 11:30 or Sun)
 * 2. The LD or OPW is critical for this weekend (falls on Sat/Sun/Mon AND
 *    lands between now and the end of the closing Monday)
 */
export function shouldTriggerWeekendAlert(params: {
  fareValidityDeadline: Date | null
  now?: Date
  ticketingDeadline: Date | null
}): boolean {
  const now = params.now ?? new Date()
  if (!isWeekendAlertWindow(now)) return false

  const ld = params.ticketingDeadline
  const opw = params.fareValidityDeadline

  return (
    (ld !== null && isDeadlineCriticalForWeekend(ld, now)) ||
    (opw !== null && isDeadlineCriticalForWeekend(opw, now))
  )
}
